import { Link } from 'react-router';
import { UserRankBadge } from '../../components/UserRankBadge.js';
import type { RankingEntry, RankingCategory } from './RankingView.js';

interface RankingListProps {
  entries: RankingEntry[];
  myUserId: string | null;
  category: RankingCategory;
}

const PODIUM_CONFIG = {
  1: {
    gradient: 'linear-gradient(150deg, #b8860b 0%, #f5c842 60%, #ffe680 100%)',
    shadow: '0 6px 24px rgba(197,158,30,0.45)',
    medal: '🥇',
    minHeight: 148,
  },
  2: {
    gradient: 'linear-gradient(150deg, #6b7a8d 0%, #b0bec5 60%, #cfd8dc 100%)',
    shadow: '0 4px 16px rgba(100,130,150,0.35)',
    medal: '🥈',
    minHeight: 124,
  },
  3: {
    gradient: 'linear-gradient(150deg, #8b5e3c 0%, #c8906a 60%, #e0b090 100%)',
    shadow: '0 4px 16px rgba(130,85,45,0.35)',
    medal: '🥉',
    minHeight: 108,
  },
} as const;

function PodiumCard({
  entry,
  isMe,
  category,
}: {
  entry: RankingEntry;
  isMe: boolean;
  category: RankingCategory;
}) {
  const cfg = PODIUM_CONFIG[entry.rank as 1 | 2 | 3];
  const initials = entry.nickname.slice(0, 2).toUpperCase();

  return (
    <Link
      to={`/users/${entry.userId}`}
      className="flex flex-col items-center gap-1.5 rounded-2xl px-2 pb-3 pt-3 relative overflow-hidden"
      style={{ background: cfg.gradient, boxShadow: cfg.shadow, minHeight: cfg.minHeight }}
    >
      {/* 光沢オーバーレイ */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 55%)',
        }}
      />
      <span className="text-2xl relative z-10">{cfg.medal}</span>
      {entry.avatarUrl ? (
        <img
          src={entry.avatarUrl}
          alt={entry.nickname}
          className="w-11 h-11 rounded-full object-cover border-2 border-white/60 flex-shrink-0 relative z-10"
        />
      ) : (
        <div className="w-11 h-11 rounded-full bg-white/25 border-2 border-white/60 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 relative z-10">
          {initials}
        </div>
      )}
      <p className="text-[11px] font-bold text-white text-center leading-tight w-full px-1 truncate relative z-10">
        {entry.nickname}
        {isMe && <span className="opacity-80"> ★</span>}
      </p>
      <p className="text-base font-bold text-white relative z-10 leading-none">{entry.score}</p>
      <p className="text-[10px] text-white/70 relative z-10">
        {category === 'checkin' ? 'チェックイン' : 'スポット'}
      </p>
    </Link>
  );
}

function ListItem({
  entry,
  isMe,
  category,
}: {
  entry: RankingEntry;
  isMe: boolean;
  category: RankingCategory;
}) {
  return (
    <Link
      to={`/users/${entry.userId}`}
      className={`flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors ${
        isMe ? 'bg-blue-50 hover:bg-blue-100/70' : ''
      }`}
    >
      <span className="text-sm text-gray-400 w-7 text-center flex-shrink-0 font-medium">
        {entry.rank}
      </span>

      {entry.avatarUrl ? (
        <img
          src={entry.avatarUrl}
          alt={entry.nickname}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
          {entry.nickname.slice(0, 2).toUpperCase()}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <span
          className={`text-sm font-medium truncate block ${isMe ? 'text-blue-700' : 'text-gray-900'}`}
        >
          {entry.nickname}
          {isMe && <span className="ml-1 text-xs text-primary">（自分）</span>}
        </span>
        <div className="flex items-center gap-1 mt-0.5">
          <UserRankBadge rank={entry.checkinRank} type="checkin" size="sm" />
          <UserRankBadge rank={entry.spotMakerRank} type="spotMaker" size="sm" />
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        <div className={`text-sm font-bold ${isMe ? 'text-blue-700' : 'text-gray-800'}`}>
          {entry.score}
        </div>
        <div className="text-[10px] text-gray-400">
          {category === 'checkin' ? 'チェックイン' : 'スポット'}
        </div>
      </div>
    </Link>
  );
}

export function RankingList({ entries, myUserId, category }: RankingListProps) {
  if (entries.length === 0) return null;

  const top3 = entries.filter((e) => e.rank <= 3);
  const rest = entries.filter((e) => e.rank > 3);

  // 表彰台の表示順: 2位・1位・3位
  const podiumOrder = [
    top3.find((e) => e.rank === 2),
    top3.find((e) => e.rank === 1),
    top3.find((e) => e.rank === 3),
  ].filter((e): e is RankingEntry => e !== undefined);

  return (
    <div>
      {/* 表彰台（TOP3）*/}
      {podiumOrder.length > 0 && (
        <div className="grid grid-cols-3 gap-2 items-end mb-4 px-1">
          {podiumOrder.map((entry) => (
            <PodiumCard
              key={entry.userId}
              entry={entry}
              isMe={entry.userId === myUserId}
              category={category}
            />
          ))}
        </div>
      )}

      {/* 4位以降 */}
      {rest.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {rest.map((entry) => (
            <ListItem
              key={entry.userId}
              entry={entry}
              isMe={entry.userId === myUserId}
              category={category}
            />
          ))}
        </div>
      )}
    </div>
  );
}
